import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Download, Calendar, MapPin, Ticket, Mail, CalendarPlus, RotateCcw } from 'lucide-react';

import { BACKEND_URL } from '../lib/api';
import DemoModeBadge from '../components/DemoModeBadge';

const MyTicketsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [refundOrderId, setRefundOrderId] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [myRefunds, setMyRefunds] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
    fetchRefunds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const fetchRefunds = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/users/me/refund-requests`, { withCredentials: true });
      setMyRefunds(res.data?.refund_requests || []);
    } catch {
      // non-blocking
    }
  };

  const submitRefundRequest = async () => {
    if (refundReason.trim().length < 10) {
      toast.error('Please write at least 10 characters explaining your request.');
      return;
    }
    setSubmittingRefund(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/orders/${refundOrderId}/refund-request`,
        { reason: refundReason.trim() },
        { withCredentials: true },
      );
      toast.success('Refund request submitted. The organiser will review it shortly.');
      setRefundOrderId(null);
      setRefundReason('');
      fetchRefunds();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit refund request.');
    } finally {
      setSubmittingRefund(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/me/orders`, {
        withCredentials: true,
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Could not load your tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (orderId) => {
    setDownloadingId(orderId);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/orders/${orderId}/tickets/pdf`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `VinePass_Tickets_${orderId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download PDF. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const requestDeleteAccount = async () => {
    if (!window.confirm('We will email a confirmation link to permanently delete your VinePass account. The link is valid for 24 hours. Continue?')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/auth/request-delete-account`, {}, { withCredentials: true });
      toast.success('Confirmation email sent. Click the link in your inbox to permanently delete your account.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send confirmation email.');
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
        <div className="text-[#4A5D50]">Loading your tickets...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1
              className="text-4xl sm:text-5xl font-bold text-[#112318]"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="my-tickets-title"
            >
              My Tickets
            </h1>
            <DemoModeBadge />
          </div>
          <p className="text-[#4A5D50]">
            Re-download your PDF tickets and view your past bookings.
          </p>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-[#E5E9E6]" data-testid="no-tickets-state">
            <Ticket className="w-12 h-12 text-[#4A5D50] mx-auto mb-4" />
            <p className="text-[#4A5D50] mb-2">You don't have any tickets yet.</p>
            <p className="text-xs text-[#4A5D50] mb-6">
              Tickets booked with <span className="font-semibold">{user?.email}</span> will appear here.
            </p>
            <Button
              onClick={() => navigate('/events')}
              className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-8"
              data-testid="browse-events-button"
            >
              Browse Events
            </Button>
          </div>
        ) : (
          <div className="space-y-5" data-testid="orders-list">
            {orders.map((order) => {
              const totalTickets = order.items.reduce(
                (sum, it) => sum + (it.tickets?.length || 0),
                0
              );
              const firstItem = order.items[0] || {};
              return (
                <div
                  key={order.order_id}
                  className="bg-white rounded-2xl p-6 border border-[#E5E9E6] shadow-sm"
                  data-testid={`order-card-${order.order_id.slice(0, 8)}`}
                >
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                    <div className="flex-1">
                      <p className="text-xs text-[#4A5D50] uppercase tracking-wider mb-1 flex items-center gap-2">
                        <span>Order #{order.order_id.slice(0, 8).toUpperCase()}</span>
                        {order.is_comp && (
                          <span className="bg-[#C9A27A]/20 text-[#9C7434] text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#C9A27A]/40" data-testid={`comp-badge-${order.order_id.slice(0, 8)}`}>
                            Comp
                          </span>
                        )}
                      </p>
                      <h3
                        className="text-2xl font-semibold text-[#112318] mb-3"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        {firstItem.event_title}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-[#4A5D50] mb-2">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          {firstItem.event_date} at {firstItem.event_time}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4" />
                          {firstItem.event_location}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Ticket className="w-4 h-4" />
                          {totalTickets} ticket{totalTickets !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <p className="text-xs text-[#4A5D50] flex items-center gap-1.5">
                        <Mail className="w-3 h-3" />
                        Sent to {order.customer_email}
                      </p>
                    </div>

                    <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between lg:justify-start gap-2 lg:gap-2">
                      <p className="text-2xl font-bold text-[#1F3D2B]">
                        R{order.total_amount?.toFixed(2)}
                      </p>
                      <Button
                        onClick={() => handleDownloadPDF(order.order_id)}
                        disabled={downloadingId === order.order_id}
                        className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-6"
                        data-testid={`download-pdf-${order.order_id.slice(0, 8)}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {downloadingId === order.order_id ? 'Preparing...' : 'Download PDF'}
                      </Button>
                    </div>
                    <div className="flex flex-wrap lg:flex-col items-start lg:items-end gap-2 lg:gap-2 lg:mt-2">
                      {firstItem.event_id && (
                        <a
                          href={`${BACKEND_URL}/api/events/${firstItem.event_id}/ics`}
                          className="inline-flex items-center gap-1.5 text-xs text-[#1F3D2B] hover:underline"
                          data-testid={`add-to-calendar-${order.order_id.slice(0, 8)}`}
                        >
                          <CalendarPlus className="w-3.5 h-3.5" />
                          Add to Calendar
                        </a>
                      )}
                      {(() => {
                        const isFreeOrComp = order.is_comp || Number(order.total_amount || 0) <= 0;
                        if (isFreeOrComp) {
                          return (
                            <span className="inline-flex items-center gap-1.5 text-xs text-[#9AA89F]" data-testid={`refund-na-${order.order_id.slice(0, 8)}`}>
                              {order.is_comp ? 'Complimentary · not refundable' : 'Free booking · not refundable'}
                            </span>
                          );
                        }
                        const refund = myRefunds.find((r) => r.order_id === order.order_id);
                        if (refund) {
                          const STATUS_TONE = {
                            pending: 'text-amber-700',
                            pending_organiser_review: 'text-amber-700',
                            approved: 'text-violet-700',
                            pending_admin_processing: 'text-violet-700',
                            processing_payfast: 'text-indigo-700',
                            refunded_payfast: 'text-emerald-700',
                            manual_eft_required: 'text-orange-700',
                            manual_eft_paid: 'text-emerald-700',
                            failed: 'text-rose-700',
                            cancelled: 'text-stone-500',
                            rejected: 'text-rose-700',
                            rejected_by_organiser: 'text-rose-700',
                          };
                          const STATUS_LABEL = {
                            pending: 'Pending review',
                            pending_organiser_review: 'Pending organiser review',
                            approved: 'Awaiting VinePass',
                            pending_admin_processing: 'Awaiting VinePass',
                            processing_payfast: 'Processing (PayFast)',
                            refunded_payfast: 'Refunded',
                            manual_eft_required: 'Banking details needed',
                            manual_eft_paid: 'EFT paid',
                            failed: 'Failed — contact support',
                            cancelled: 'Cancelled',
                            rejected: 'Rejected',
                            rejected_by_organiser: 'Rejected',
                          };
                          const tone = STATUS_TONE[refund.status] || 'text-stone-700';
                          const label = STATUS_LABEL[refund.status] || refund.status;
                          // If manual_eft_required, link to bank submission page.
                          if (refund.status === 'manual_eft_required') {
                            return (
                              <Link
                                to={`/refund-requests/${refund.refund_id}/bank-details`}
                                className="inline-flex items-center gap-1.5 text-xs text-orange-700 font-medium underline hover:no-underline"
                                data-testid={`refund-eft-action-${order.order_id.slice(0, 8)}`}
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Action needed — submit bank details
                              </Link>
                            );
                          }
                          return (
                            <span className={`inline-flex items-center gap-1.5 text-xs ${tone}`} data-testid={`refund-status-${order.order_id.slice(0, 8)}`}>
                              <RotateCcw className="w-3.5 h-3.5" />
                              Refund · {label}
                            </span>
                          );
                        }
                        return (
                          <button
                            type="button"
                            onClick={() => { setRefundOrderId(order.order_id); setRefundReason(''); }}
                            className="inline-flex items-center gap-1.5 text-xs text-[#1F3D2B] hover:underline"
                            data-testid={`request-refund-${order.order_id.slice(0, 8)}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Request Refund
                          </button>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Ticket codes preview */}
                  {firstItem.tickets && firstItem.tickets.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-[#E5E9E6]">
                      <p className="text-xs text-[#4A5D50] uppercase tracking-wider mb-2">
                        Ticket Codes
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {order.items.flatMap((it) =>
                          (it.tickets || []).map((t) => (
                            <span
                              key={t.ticket_code}
                              className="text-xs font-mono bg-[#F4F6F3] text-[#1F3D2B] px-2.5 py-1 rounded border border-[#E5E9E6]"
                            >
                              {t.ticket_code}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Account settings — quiet section at bottom */}
        <div className="mt-12 pt-8 border-t border-stone-200 text-center">
          <p className="text-xs uppercase tracking-[0.18em] text-stone-400 mb-3">Account</p>
          <button
            type="button"
            onClick={requestDeleteAccount}
            data-testid="delete-account-button"
            className="text-xs text-stone-500 hover:text-rose-700 underline"
          >
            Delete my account
          </button>
        </div>

        {/* Refund Request Modal */}
        {refundOrderId && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            data-testid="refund-modal"
            onClick={() => !submittingRefund && setRefundOrderId(null)}
          >
            <div
              className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-serif text-2xl text-[#1F3D2B] mb-2">Request a Refund</h3>
              <p className="text-sm text-stone-600 mb-4">
                Our <Link to="/refund-policy" className="underline hover:no-underline">refund policy</Link> applies. Booking fees are non-refundable, and requests must be made at least 7 days before the event.
              </p>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                rows={4}
                placeholder="Briefly explain why you are requesting a refund (minimum 10 characters)…"
                className="w-full border border-stone-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3D2B]/40"
                data-testid="refund-reason-input"
              />
              <p className="text-[11px] text-stone-500 mt-2">
                Approved refunds are processed within <strong className="text-[#1F3D2B]">7–10 business days</strong> back to your original payment method. Booking fees are non-refundable.
              </p>
              <div className="flex justify-end gap-2 mt-5">
                <Button
                  variant="ghost"
                  onClick={() => { setRefundOrderId(null); setRefundReason(''); }}
                  disabled={submittingRefund}
                  data-testid="refund-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitRefundRequest}
                  disabled={submittingRefund || refundReason.trim().length < 10}
                  className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
                  data-testid="refund-submit"
                >
                  {submittingRefund ? 'Submitting…' : 'Submit Request'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTicketsPage;
